<template>
  <div class="page-container animate-fade-in">
    <PageHeader
      title="Resume & Skills"
      description="Edit work experience, education, skills, and resume content."
      :breadcrumbs="[{ label: 'Admin', to: '/admin' }, { label: 'Resume' }]"
    />

    <form class="space-y-6" @submit.prevent="save">
      <BaseCard title="Professional summary">
        <div class="space-y-5 mt-2">
          <BaseTextarea v-model="form.summary" label="Summary" :rows="4" />
          <BaseTextarea v-model="form.objectives" label="Objectives" :rows="4" />
        </div>
      </BaseCard>

      <BaseCard title="Work experience">
        <div class="space-y-6 mt-2">
          <div v-for="(job, index) in form.experience" :key="index" class="p-4 rounded-xl glass-subtle space-y-4">
            <div class="flex justify-between items-center">
              <h4 class="font-medium">Job {{ index + 1 }}</h4>
              <BaseButton variant="ghost" type="button" class="text-danger" @click="removeJob(index)">
                <AppIcon name="Trash2" :size="16" />
              </BaseButton>
            </div>
            <div class="grid sm:grid-cols-2 gap-4">
              <BaseInput v-model="job.role" label="Role" />
              <BaseInput v-model="job.company" label="Company" />
              <BaseInput v-model="job.period" label="Period" />
              <BaseInput v-model="job.duration" label="Duration" />
              <BaseInput v-model="job.location" label="Location" class="sm:col-span-2" />
            </div>
            <div>
              <p class="text-label mb-2">Highlights (one per line)</p>
              <BaseTextarea
                :model-value="job.highlights.join('\n')"
                :rows="4"
                @update:modelValue="job.highlights = $event.split('\n').filter(Boolean)"
              />
            </div>
          </div>
          <BaseButton variant="secondary" size="sm" type="button" @click="addJob">
            <AppIcon name="Plus" :size="16" />
            Add experience
          </BaseButton>
        </div>
      </BaseCard>

      <BaseCard title="Education">
        <div class="space-y-4 mt-2">
          <div v-for="(edu, index) in form.education" :key="index" class="p-4 rounded-xl glass-subtle grid sm:grid-cols-2 gap-4">
            <BaseInput v-model="edu.level" label="Level" />
            <BaseInput v-model="edu.period" label="Period" />
            <BaseInput v-model="edu.school" label="School" class="sm:col-span-2" />
            <BaseInput v-model="edu.location" label="Location" class="sm:col-span-2" />
            <BaseButton variant="ghost" type="button" class="text-danger sm:col-span-2 justify-self-start" @click="removeEducation(index)">
              Remove
            </BaseButton>
          </div>
          <BaseButton variant="secondary" size="sm" type="button" @click="addEducation">Add education</BaseButton>
        </div>
      </BaseCard>

      <BaseCard title="Skill categories" description="Add, rename, or remove skill groups shown on the About page.">
        <AdminSkillGroups v-model="form.skillGroups" class="mt-2" />
      </BaseCard>

      <BaseCard title="Lists">
        <div class="grid lg:grid-cols-2 gap-6 mt-2">
          <div>
            <p class="text-label mb-2">Core competencies</p>
            <AdminStringList v-model="form.personalSkills" add-label="Add competency" />
          </div>
          <div>
            <p class="text-label mb-2">Hobbies / interests</p>
            <AdminStringList v-model="form.hobbies" add-label="Add hobby" />
          </div>
        </div>
      </BaseCard>

      <div class="flex justify-end gap-3">
        <BaseButton variant="secondary" type="button" tag="router-link" to="/admin">Cancel</BaseButton>
        <BaseButton variant="primary" type="submit" :loading="saving">Save changes</BaseButton>
      </div>
    </form>
  </div>
</template>

<script>
import { siteState, saveSiteData } from '@/composables/useSiteData'
import { useToast } from '@/composables/useToast'
import PageHeader from '@/components/layout/PageHeader.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseTextarea from '@/components/ui/BaseTextarea.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import AdminStringList from '@/components/admin/AdminStringList.vue'
import AdminSkillGroups from '@/components/admin/AdminSkillGroups.vue'
import { normalizeSkillGroups } from '@/utils/skillGroups'

export default {
  name: 'AdminResume',
  components: {
    PageHeader,
    BaseCard,
    BaseInput,
    BaseTextarea,
    BaseButton,
    AppIcon,
    AdminStringList,
    AdminSkillGroups,
  },
  data() {
    return {
      saving: false,
      form: {
        summary: '',
        objectives: '',
        experience: [],
        education: [],
        skillGroups: [],
        personalSkills: [],
        hobbies: [],
      },
    }
  },
  created() {
    this.loadForm()
  },
  methods: {
    loadForm() {
      this.form = {
        summary: siteState.profile.informations.summary,
        objectives: siteState.profile.informations.objectives,
        experience: JSON.parse(JSON.stringify(siteState.experience)),
        education: JSON.parse(JSON.stringify(siteState.education)),
        skillGroups: normalizeSkillGroups(siteState.skills),
        personalSkills: [...siteState.personalSkills],
        hobbies: [...siteState.hobbies],
      }
    },
    addJob() {
      this.form.experience.push({
        role: '',
        company: '',
        period: '',
        duration: '',
        location: '',
        highlights: [],
      })
    },
    removeJob(index) {
      this.form.experience.splice(index, 1)
    },
    addEducation() {
      this.form.education.push({ level: '', period: '', school: '', location: '' })
    },
    removeEducation(index) {
      this.form.education.splice(index, 1)
    },
    async save() {
      this.saving = true
      const { success, error } = useToast()
      try {
        const profile = JSON.parse(JSON.stringify(siteState.profile))
        profile.informations.summary = this.form.summary
        profile.informations.objectives = this.form.objectives
        await saveSiteData({
          profile,
          experience: this.form.experience,
          education: this.form.education,
          skills: this.form.skillGroups
            .map((group) => ({
              ...group,
              title: group.title.trim() || 'Skills',
              items: group.items.filter((item) => item.name?.trim()),
            }))
            .filter((group) => group.title || group.items.length),
          personalSkills: this.form.personalSkills.filter(Boolean),
          hobbies: this.form.hobbies.filter(Boolean),
        })
        success('Resume saved', 'Resume and skills were updated.')
      } catch (e) {
        error('Save failed', 'Could not save resume data.')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
